// BUILD-046 cycle C (Edit 3): the searchable Customer / Job choice of the toolbar's Filter popover. It replaces the native drop-down of every job.
//
// The popover shows: the name of the control, the two quick choices (All jobs, No job), a search box (role=combobox), and a short hint. Matches are listed ONLY after
// something is typed (at least one character), at most eight, with "Showing 8 of N. Keep typing to narrow." when more match. Arrow keys move through the matches,
// Enter chooses the highlighted one, Escape closes the list (a second Escape closes the popover), and Tab only leaves the field: it NEVER chooses a job.
// The sentence under the box says which tasks the jobs come from (the loaded ones) and never claims to search every task.
// All rules (the options, the search, the wording, the filter a choice stands for) are in jobFilterModel.ts; the key rules are jobFieldModel.decideJobKey; this file draws them.
import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { JOB_FILTER_LABEL, JOB_QUICK_CHOICES, describeJobPicker, jobChoiceNote, jobFilterFromOption, jobFilterFromQuick, jobFilterSummary, searchJobOptions } from './jobFilterModel';
import type { JobChoiceOption, JobFilter } from './jobFilterModel';
import { decideJobKey, jobOptionId } from './jobFieldModel';
import { closeList, initialCombo, typeQuery } from './peopleComboboxModel';
import type { ComboState } from './peopleComboboxModel';
import { stripTestMarker } from './displayNames';

export interface JobPickerControl {
  /** The id prefix, unique per view (the search box is `${idPrefix}-job`). */
  idPrefix: string;
  /** The filter now: `any`, `none`, or one job (the stable id is the value). */
  filter: JobFilter;
  /** The jobs on the rows the view has loaded (jobFilterModel.jobChoicesOf). */
  options: readonly JobChoiceOption[];
  /** Where the jobs come from (jobFilterModel.jobFilterScopeText). */
  scopeText: string;
  onChange: (next: JobFilter) => void;
  /** BUILD-046 (Customer association): the visible name of the control. Absent, it is "Customer / Job" exactly as before; the page gives "Job" while the Customer picker is also offered, so the two never share a name. */
  label?: string;
}

const QUICK_BASE = 'rounded-full border px-3 py-1 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]';

export function JobFilterPicker({ idPrefix, filter, options, scopeText, onChange, label = JOB_FILTER_LABEL }: JobPickerControl) {
  const [combo, setCombo] = useState<ComboState>(initialCombo());
  // What was just chosen, said once in the polite line when the list is closed ("Filtering by Smith Residence.").
  const [chosenNote, setChosenNote] = useState('');
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const inputId = `${idPrefix}-job`;
  const labelId = `${inputId}-label`;
  const listboxId = `${inputId}-listbox`;
  const moreId = `${inputId}-more`;
  const scopeId = `${inputId}-scope`;

  const outcome = searchJobOptions(options, combo.query);
  const view = describeJobPicker(outcome);
  const matches = outcome.matches;
  const showList = combo.open && outcome.status !== 'idle';
  const active = showList && combo.active >= 0 && combo.active < matches.length ? matches[combo.active] : null;
  const describedBy = [scopeText !== '' ? scopeId : undefined, showList && view.moreNote !== null ? moreId : undefined].filter(Boolean).join(' ');
  // Browser run (cycle C): eight two-line matches are taller than the list's own scroll area, so the arrow keys could highlight a match that was out of sight. The highlighted
  // match is brought into view (the list, and the page behind it, scroll only as far as needed).
  const activeJobId = active !== null ? active.jobId : null;
  useEffect(() => {
    if (activeJobId === null) return;
    document.getElementById(jobOptionId(inputId, activeJobId))?.scrollIntoView?.({ block: 'nearest' });
  }, [activeJobId, inputId]);

  function choose(jobId: string): void {
    const next = jobFilterFromOption(matches.find((m) => m.jobId === jobId));
    if (next === null) return;
    onChange(next);
    setChosenNote(`Filtering by ${jobFilterSummary(next)}.`);
    setCombo(initialCombo());
  }

  function quick(value: 'any' | 'none'): void {
    const next = jobFilterFromQuick(value);
    onChange(next);
    setChosenNote(value === 'any' ? 'Showing all jobs.' : 'Showing tasks with no job.');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    // With no list on screen (nothing typed) Escape is the popover's own: it closes the popover.
    if (event.key === 'Escape' && !showList) {
      if (combo.open) setCombo((state) => closeList(state));
      return;
    }
    const decision = decideJobKey(event.key, combo, matches);
    if (decision.handled) event.preventDefault();
    // The Escape that closes the list is not also the popover's Escape.
    if (decision.stop) event.stopPropagation();
    if (decision.choose !== null) {
      choose(decision.choose);
      return;
    }
    if (decision.state !== combo) setCombo(decision.state);
  }

  return (
    <div ref={wrapRef} data-testid="job-filter">
      <p id={labelId} className="mb-1 text-xs font-semibold text-gray-700">
        {label}
      </p>
      <div role="group" aria-label={`${label} quick choices`} className="mb-2 flex flex-wrap gap-2">
        {JOB_QUICK_CHOICES.map((choice) => {
          const selected = filter.kind === choice.value;
          return (
            <button
              key={choice.value}
              type="button"
              aria-pressed={selected}
              onClick={() => quick(choice.value)}
              className={`${QUICK_BASE} ${selected ? 'border-[#2a7fb8] bg-sky-100 text-[#14537d]' : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'}`}
              data-testid={`${inputId}-quick-${choice.value}`}
            >
              {choice.label}
            </button>
          );
        })}
      </div>
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-labelledby={labelId}
          aria-expanded={showList}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={active !== null ? jobOptionId(inputId, active.jobId) : undefined}
          aria-describedby={describedBy}
          autoComplete="off"
          value={combo.query}
          placeholder={label === JOB_FILTER_LABEL ? 'Search by customer or job name' : 'Search by job name'}
          onChange={(event) => {
            setChosenNote('');
            setCombo((state) => typeQuery(state, event.target.value));
          }}
          onKeyDown={onKeyDown}
          onBlur={(event) => {
            // Moving into the list keeps it; leaving the picker closes it. The typed text stays.
            const next = event.relatedTarget;
            if (!(next instanceof Node && wrapRef.current !== null && wrapRef.current.contains(next))) setCombo((state) => closeList(state));
          }}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 placeholder:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
        />
        {/* The listing is part of the panel (not a floating layer), so the popover never clips it. */}
        {showList && (
          <div className="mt-1 rounded-md border border-gray-200 bg-white" onMouseDown={(event) => event.preventDefault()}>
            <ul id={listboxId} role="listbox" aria-label="Jobs" className="max-h-72 overflow-y-auto py-1" data-testid={`${inputId}-listbox`}>
              {matches.map((job, index) => {
                const note = jobChoiceNote(job);
                const detail = [stripTestMarker(job.customer), stripTestMarker(job.code)].filter((part) => part !== '').join(' \u00b7 ');
                const current = filter.kind === 'job' && filter.jobId === job.jobId;
                return (
                  <li
                    key={job.jobId}
                    id={jobOptionId(inputId, job.jobId)}
                    role="option"
                    aria-selected={current}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => choose(job.jobId)}
                    onMouseMove={() => setCombo((state) => (state.active === index ? state : { ...state, active: index }))}
                    className={`cursor-pointer select-none px-3 py-1.5 text-sm text-gray-900 ${index === combo.active ? 'bg-sky-100' : current ? 'bg-sky-50 hover:bg-sky-100' : 'hover:bg-gray-50'}`}
                  >
                    <span className="block break-words">{stripTestMarker(job.label)}</span>
                    {(note !== null || detail !== '') && <span className="block text-xs text-gray-500">{[detail, note].filter((part) => part !== '' && part !== null).join(' \u00b7 ')}</span>}
                  </li>
                );
              })}
              {matches.length === 0 && (
                <li role="presentation" className="px-3 py-2 text-sm text-gray-600">
                  {view.emptyText}
                </li>
              )}
            </ul>
            {view.moreNote !== null && (
              <p id={moreId} className="border-t border-gray-100 px-3 py-1 text-xs text-gray-700" data-testid={`${inputId}-more`}>
                {view.moreNote}
              </p>
            )}
          </div>
        )}
      </div>
      <div role="status" aria-live="polite" className="sr-only">
        {showList ? view.announcement : chosenNote}
      </div>
      {scopeText !== '' && (
        <p id={scopeId} className="mt-2 text-xs text-gray-600" data-testid={`${inputId}-scope`}>
          {scopeText}
        </p>
      )}
    </div>
  );
}
